import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import Loading from '../components/Loading';
import API_BASE_URL from '../config/baseUrl';
import { useGetCasinoRoundsQuery } from '../services/casino/casinoApi';

// Fetch casino bets by table ID
const fetchCasinoBetsByTableId = async function(tableId, isActive) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/get-casino-bets-by-tableid",
      { tableId: tableId, isActive: isActive },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch casino bets" };
    }

    return { success: true, data: data.data || [] };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch casino bets";
    return { success: false, error: errorMessage };
  }
};

const CasinoDisplayGamesPage = () => {
  const navigate = useNavigate();
  const { tableId, gameName } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [casinoBets, setCasinoBets] = useState([]);
  const [page, setPage] = useState(1);
  
  // Fetch casino rounds using API
  const { data: roundsData, isLoading: roundsLoading, error: roundsError } = useGetCasinoRoundsQuery({
    game: tableId || 'teen20',
    page: page,
    limit: 20
  }, {
    pollingInterval: 5000, // Poll every 5 seconds
  });
  
  // Calculate total P/L from API data
  const total = roundsData?.summary?.overallPL || 0;
  const rounds = roundsData?.data || [];
  const totalPages = roundsData?.pagination?.totalPages || roundsData?.totalPages || 1;

  // Fetch data on mount (legacy code - keeping for backward compatibility)
  useEffect(function() {
    
    const loadData = async function() {
      setLoading(true);
      setError(null);

      const result = await fetchCasinoBetsByTableId(tableId, true);

      if (result.success) {
        setCasinoBets(result.data);
      } else {
        setError(result.error);
        setCasinoBets([]);
      }

      setLoading(false);
    };

    if (tableId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [tableId]);

  // Handle Show Bet button click
  const handleShowBet = function(roundId) {
    navigate('/casino/all-bets/' + roundId);
  };

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-6xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal">
                {decodeURIComponent(gameName || 'Teenpatti 2020')}
              </span>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors"
              >
                Back
              </button>
            </div>

            {/* Total Display */}
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <div className="text-sm text-gray-600 mb-1">Total</div>
              <div className={`text-3xl font-semibold ${total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {roundsLoading && rounds.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : roundsError ? (
                <div className="p-4 text-red-500 text-center">{roundsError?.message || 'Error loading data'}</div>
              ) : (
                <table className={`w-full border-collapse text-sm ${roundsLoading ? 'opacity-50' : ''}`}>
                  <thead>
                    <tr className="bg-[#3e525f] text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">#</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Event ID</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Open Date</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">Winner</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-gray-600">PL</th>
                      <th className="py-3 px-4 text-left font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {rounds.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center border-t border-gray-200">
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-16 h-16 mb-4" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                              <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                <ellipse className="fill-gray-200" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                <g className="fill-gray-300" fillRule="nonzero">
                                  <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                  <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"></path>
                                </g>
                              </g>
                            </svg>
                            <div className="text-gray-500">No data</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      rounds.map(function(round, index) {
                        return (
                          <tr key={round.roundId} className="hover:bg-gray-50 border-t border-gray-200">
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{(page - 1) * 20 + index + 1}</td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{round.roundId}</td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                              {round.openDate ? new Date(round.openDate).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                              }).replace(/(\d{2})\/(\d{2})\/(\d{4}),/, '$3-$2-$1') : '-'}
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-r border-gray-200">{round.winner || '-'}</td>
                            <td className="py-3 px-4 border-r border-gray-200">
                              <span className={`font-semibold ${round.pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {round.pl?.toFixed(2) || '0.00'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <button
                                onClick={function() { handleShowBet(round.roundId); }}
                                className="bg-[#3e525f] text-white px-4 py-1.5 text-sm hover:bg-[#2d3f4a] transition-colors"
                              >
                                Show Bets
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={function() { setPage(function(p) { return Math.max(1, p - 1); }); }}
                    disabled={page === 1 || roundsLoading}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Prev
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, function(_, i) {
                    let p;
                    if (totalPages <= 5) {
                      p = i + 1;
                    } else if (page <= 3) {
                      p = i + 1;
                    } else if (page >= totalPages - 2) {
                      p = totalPages - 4 + i;
                    } else {
                      p = page - 2 + i;
                    }
                    return (
                      <button
                        key={p}
                        onClick={function() { setPage(p); }}
                        disabled={roundsLoading}
                        className={`px-3 py-1.5 text-sm border rounded transition-colors ${
                          p === page
                            ? 'bg-[#3e525f] text-white border-[#3e525f]'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={function() { setPage(function(p) { return Math.min(totalPages, p + 1); }); }}
                    disabled={page === totalPages || roundsLoading}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CasinoDisplayGamesPage;